// LibraryPage — Browse journeys, topics, and subtopics
// Simplified in Phase 3: pure browsing, no inline CRUD

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLearningJourneys } from '../../hooks/useLearningJourneys';
import { getTopicsByJourney } from '../../services/firebase';
import Layout from '../layout/Layout';
import Card from '../ui/Card';
import Button from '../ui/Button';
import EmptyState from '../ui/EmptyState';
import type { Topic, Journey } from '../../types';

// Helper: count total subtopic touches across a topic's subtopics
const getTotalTouches = (topic: Topic): number =>
  (topic.subtopics || []).reduce((sum, sub) => sum + (sub.touchCount || 0), 0);

// Helper: format exam date display
const formatExamInfo = (journey: Journey, t: (key: string, opts?: Record<string, unknown>) => string): string => {
  if (!journey.examDate) return '';
  const date = typeof journey.examDate === 'string' ? new Date(journey.examDate) : journey.examDate;
  const now = new Date();
  const diffDays = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return t('journeys.examPassed');
  if (diffDays === 0) return t('journeys.examToday');
  return t('journeys.daysLeft', { count: diffDays });
};

const LibraryPage: React.FC = () => {
  const { t } = useTranslation('library');
  const navigate = useNavigate();
  const { journeyId } = useParams<{ journeyId?: string }>();
  const { journeys } = useLearningJourneys();

  const [topics, setTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedJourney = journeyId ? journeys.find(j => j.id === journeyId) : null;

  // Load topics when a journey is selected
  useEffect(() => {
    if (!journeyId) {
      setTopics([]);
      return;
    }

    let cancelled = false;
    const loadTopics = async () => {
      setLoading(true);
      setError(null);
      const result = await getTopicsByJourney(journeyId);
      if (cancelled) return;
      if (result.success && result.topics) {
        setTopics(result.topics);
      } else {
        setError(result.error || t('errors.fetchTopics'));
      }
      setLoading(false);
    };

    loadTopics();
    return () => { cancelled = true; };
  }, [journeyId, t]);

  // ---- Journey list view ----
  if (!journeyId) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-neutral-dark dark:text-white">
              {t('title')}
            </h1>
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate('/journeys/new')}
            >
              {t('journeys.createJourney')}
            </Button>
          </div>

          {journeys.length === 0 ? (
            <EmptyState
              type="journeys"
              title={t('journeys.noJourneys')}
              description={t('journeys.noJourneysDescription')}
              actionText={t('journeys.createJourney')}
              onAction={() => navigate('/journeys/new')}
            />
          ) : (
            <div className="space-y-3">
              {journeys
                .filter(j => j.status === 'active')
                .map(journey => (
                  <Card
                    key={journey.id}
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => navigate(`/library/${journey.id}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-neutral-dark dark:text-white truncate">
                          {journey.title || journey.subject}
                        </h3>
                        <div className="flex items-center gap-3 mt-1 text-sm text-neutral-medium">
                          {journey.topicIds && (
                            <span>
                              {t('journeys.topics', { count: journey.topicIds.length })}
                            </span>
                          )}
                          {journey.examDate && (
                            <span>{formatExamInfo(journey, t)}</span>
                          )}
                        </div>
                      </div>
                      <svg className="w-5 h-5 text-neutral-medium flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </Card>
                ))}
            </div>
          )}
        </div>
      </Layout>
    );
  }

  // ---- Topics view for selected journey ----
  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header with back navigation */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/library')}
            className="text-sm text-primary-600 hover:text-primary-700 mb-2 flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('actions.backToJourneys')}
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-neutral-dark dark:text-white">
                {selectedJourney?.title || selectedJourney?.subject || t('title')}
              </h1>
              {selectedJourney?.examDate && (
                <p className="text-sm text-neutral-medium mt-1">
                  {formatExamInfo(selectedJourney, t)}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(`/library/journey/${journeyId}`)}
              >
                {t('journeys.managePlan')}
              </Button>
            </div>
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="text-center py-12 text-neutral-medium">
            {t('common:loading')}
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 p-4 rounded-lg mb-4">
            {error}
          </div>
        )}

        {/* Empty topics */}
        {!loading && !error && topics.length === 0 && (
          <EmptyState
            type="topics"
            title={t('topics.noTopicsInJourney')}
            description={t('topics.noTopicsDescription')}
            actionText={t('journeys.managePlan')}
            onAction={() => navigate(`/library/journey/${journeyId}`)}
          />
        )}

        {/* Topic list */}
        {!loading && topics.length > 0 && (
          <div className="space-y-3">
            {topics.map(topic => (
              <TopicCard
                key={topic.id}
                topic={topic}
                t={t}
                journeyId={journeyId || ''}
              />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

// ---- Topic Card with expandable subtopics ----

interface TopicCardProps {
  topic: Topic;
  t: (key: string, opts?: Record<string, unknown>) => string;
  journeyId: string;
}

const TopicCard: React.FC<TopicCardProps> = ({ topic, t, journeyId }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const subtopics = topic.subtopics || [];
  const totalTouches = getTotalTouches(topic);

  return (
    <Card>
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="min-w-0 flex-1">
          <h3 className="font-medium text-neutral-dark dark:text-white">
            {topic.title}
          </h3>
          <div className="flex items-center gap-3 mt-1 text-sm text-neutral-medium">
            <span>{t('topics.subtopics', { count: subtopics.length })}</span>
            {totalTouches > 0 && (
              <span>{t('topics.sessions', { count: totalTouches })}</span>
            )}
          </div>
        </div>
        <svg
          className={`w-5 h-5 text-neutral-medium flex-shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>

      {/* Expanded subtopics */}
      {expanded && subtopics.length > 0 && (
        <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
          <ul className="space-y-2">
            {subtopics.map(sub => (
              <li
                key={sub.id}
                className="flex items-center justify-between text-sm py-1"
              >
                <span className="text-neutral-dark dark:text-neutral-light">
                  {sub.title}
                </span>
                <div className="flex items-center gap-2 text-neutral-medium text-xs">
                  {sub.touchCount && sub.touchCount > 0 && (
                    <span>
                      {sub.touchCount}x
                    </span>
                  )}
                  {sub.lastTeachingForm && (
                    <span className="px-1.5 py-0.5 rounded bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300">
                      {t(`forms.${sub.lastTeachingForm}`)}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-4 flex gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/workspace/${journeyId}/${topic.id}`);
              }}
            >
              {t('topics.openWorkspace')}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default LibraryPage;
